import { DocumentData } from 'firebase/firestore';
import { exportFileType, exportEncoding } from '../../constants/enum';
import { resolveDisplayLabel } from './noLabelKey';

const INTERNAL_KEYS = new Set([
  'id',
  'label_id',
  'bounding_boxes',
  'label_bounding_boxes',
  'page',
  'label_page',
  'confidence',
  'no_label',
]);

const isValueNode = (v: unknown): v is { value: unknown; [key: string]: unknown } =>
  typeof v === 'object' && v !== null && 'value' in (v as object);

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * free_ocr_key_order の1エントリ。
 * - string: 単一値のキー（順序情報のみ）
 * - { key, columns }: 配列（明細）キー。配列内オブジェクトの列順を持つ
 * - { key, children }: かたまり（ネスト）のキー。中のキー順を持つ（#3249）
 *
 * 旧形式（string[] = トップレベルキーのみ）も文字列エントリとして扱える。
 */
export type FreeOcrKeyOrderEntry =
  | string
  | { key: string; columns: string[] }
  | { key: string; children: FreeOcrKeyOrderEntry[] };

const orderEntryKey = (entry: FreeOcrKeyOrderEntry): string =>
  typeof entry === 'string' ? entry : entry.key;

const orderEntryChildren = (entry: FreeOcrKeyOrderEntry): FreeOcrKeyOrderEntry[] | undefined =>
  typeof entry !== 'string' && 'children' in entry ? entry.children : undefined;

/** { value, id, ... } 形式の値ノードか（かたまりと区別するために使う） */
const isValueNodeForOrder = (v: unknown): boolean => isPlainObject(v) && 'value' in v;

/**
 * result_data から表示順（free_ocr_key_order）を生成する。
 * 編集保存時に、画面の表示順（配列内の列順を含む）をそのまま保持するために使う。
 * - 配列（明細）キー → { key, columns } で列順も記録
 * - それ以外 → 文字列エントリ
 */
export const buildFreeOcrKeyOrder = (
  resultData: Record<string, unknown>,
): FreeOcrKeyOrderEntry[] => {
  return Object.keys(resultData)
    .filter(key => !INTERNAL_KEYS.has(key))
    .map(key => {
      const value = resultData[key];
      if (Array.isArray(value) && value.length > 0 && isPlainObject(value[0])) {
        const columns = Object.keys(value[0] as Record<string, unknown>).filter(
          c => !INTERNAL_KEYS.has(c),
        );
        return { key, columns };
      }
      // かたまり（値ノードでも配列でもないオブジェクト）は中のキー順も記録する（#3249）
      if (isPlainObject(value) && !isValueNodeForOrder(value)) {
        return { key, children: buildFreeOcrKeyOrder(value) };
      }
      return key;
    });
};

/** keyOrder エントリからかたまり（ネスト）内のキー順を引くマップを作る。 */
const buildChildrenOrderMap = (
  keyOrder: FreeOcrKeyOrderEntry[],
): Map<string, FreeOcrKeyOrderEntry[]> => {
  const map = new Map<string, FreeOcrKeyOrderEntry[]>();
  for (const entry of keyOrder) {
    const children = orderEntryChildren(entry);
    if (children) {
      map.set(orderEntryKey(entry), children);
    }
  }
  return map;
};

/** keyOrder エントリから配列の列順を引くマップを作る。 */
const buildColumnOrderMap = (keyOrder: FreeOcrKeyOrderEntry[]): Map<string, string[]> => {
  const map = new Map<string, string[]>();
  for (const entry of keyOrder) {
    if (typeof entry !== 'string' && 'columns' in entry) {
      map.set(entry.key, entry.columns);
    }
  }
  return map;
};

/** オブジェクトを指定した列順に並べ替える。列順に無いキーは末尾に補完する。 */
const orderObjectByColumns = (
  obj: Record<string, unknown>,
  columns: string[],
): Record<string, unknown> => {
  const ordered: Record<string, unknown> = {};
  for (const col of columns) {
    if (Object.prototype.hasOwnProperty.call(obj, col)) {
      ordered[col] = obj[col];
    }
  }
  for (const key of Object.keys(obj)) {
    if (!Object.prototype.hasOwnProperty.call(ordered, key)) {
      ordered[key] = obj[key];
    }
  }
  return ordered;
};

/**
 * フリーOCRの result_data を表示順に並べ替える。
 *
 * result_data は Firestore の map で保存されており、取得時にキー順が保証されない
 * （AIが決めた「請求番号→日付→金額」のような意味のある順序が崩れる）。
 * そのため OCR 実行時に確定したキー順を free_ocr_key_order フィールドへ別途保持しており、
 * 表示・エクスポート時はこの順序に従ってオブジェクトを再構築する。
 *
 * JavaScript のオブジェクトは文字列キーの挿入順を保持するため、並べ替えたオブジェクトを
 * そのまま Object.entries / Object.keys で回せば正しい順序になる。
 *
 * keyOrder が無い場合（既存データ・後方互換）は元の順序のまま返す。
 */
export const orderFreeOcrResultData = (
  resultData: Record<string, unknown>,
  keyOrder?: FreeOcrKeyOrderEntry[] | null,
): Record<string, unknown> => {
  if (!keyOrder || keyOrder.length === 0) return resultData;
  const columnOrderMap = buildColumnOrderMap(keyOrder);
  const childrenOrderMap = buildChildrenOrderMap(keyOrder);

  // 配列（明細）内オブジェクトの列順を keyOrder に従って並べ替える。
  // 旧形式（列順情報なし）の配列キーは columnOrderMap に載らないため、元の順序のまま。
  const applyColumnOrder = (key: string, value: unknown): unknown => {
    const columns = columnOrderMap.get(key);
    if (columns && Array.isArray(value)) {
      return value.map(item => (isPlainObject(item) ? orderObjectByColumns(item, columns) : item));
    }
    // かたまり（ネスト）は中のキー順も再帰的に復元する（#3249）。
    // 記録が無い旧データはそのまま返すため後方互換を保てる。
    const children = childrenOrderMap.get(key);
    if (children && isPlainObject(value) && !isValueNodeForOrder(value)) {
      return orderFreeOcrResultData(value, children);
    }
    return value;
  };

  const ordered: Record<string, unknown> = {};
  // keyOrder の順に既存キーを詰める（配列キーは列順、かたまりは中のキー順も適用）
  for (const entry of keyOrder) {
    const key = orderEntryKey(entry);
    if (Object.prototype.hasOwnProperty.call(resultData, key)) {
      ordered[key] = applyColumnOrder(key, resultData[key]);
    }
  }
  // keyOrder に載っていないキー（保存後に追加された等）は末尾に補完して欠落を防ぐ
  for (const key of Object.keys(resultData)) {
    if (!Object.prototype.hasOwnProperty.call(ordered, key)) {
      ordered[key] = resultData[key];
    }
  }
  return ordered;
};

/**
 * result_data を縦展開の行配列に変換する。
 * - スカラー / valueNode → [項目名, 値] の1行
 * - 配列（表） → 空行 + セクション名行 + ヘッダー行 + データ行
 */
export const buildVerticalRows = (resultData: Record<string, unknown>): [string, string][] => {
  const rows: [string, string][] = [];

  for (const [key, val] of Object.entries(resultData)) {
    if (INTERNAL_KEYS.has(key)) continue;

    // かたまり（セクション）はネストしたオブジェクトで届く（#3249）。
    // 値ノードでも配列でもないオブジェクトはかたまりとみなし、
    // セクション名の行を置いてから中身を再帰的に展開する。
    // （これを String() に渡すと [object Object] になり中身が丸ごと失われる）
    if (isPlainObject(val) && !isValueNode(val)) {
      const childRows = buildVerticalRows(val);
      if (childRows.length === 0) continue;
      // 見出しが文書に印字されているかたまりだけセクション名の行を置く。
      // 名前が無いかたまり（"_g1" 等）は空行で区切るだけにする。
      const sectionLabel = resolveDisplayLabel(key, val);
      rows.push([sectionLabel, '']);
      rows.push(...childRows);
      continue;
    }

    if (Array.isArray(val)) {
      // 配列→表セクション
      if (val.length === 0) continue;
      const firstItem = val[0] as Record<string, unknown>;
      const colKeys = Object.keys(firstItem).filter(k => !INTERNAL_KEYS.has(k));

      rows.push(['', '']); // 空行で区切り
      rows.push([key, '']); // セクションヘッダー

      // 表ヘッダー行
      rows.push(colKeys as unknown as [string, string]);

      // データ行
      (val as Record<string, unknown>[]).forEach(item => {
        const cells: string[] = [];
        for (const colKey of colKeys) {
          const cell = item[colKey];
          if (isValueNode(cell)) {
            cells.push(cell.value != null ? String(cell.value) : '');
          } else if (cell != null) {
            cells.push(String(cell));
          } else {
            cells.push('');
          }
        }
        rows.push(cells as unknown as [string, string]);
      });
    } else if (isValueNode(val)) {
      // 文書に項目名が無い行（表題・ロゴ・注記など）はキーが内部連番なので
      // 項目名の列を空にし、値だけを出力する（#3249）
      rows.push([resolveDisplayLabel(key, val), val.value != null ? String(val.value) : '']);
    } else if (val != null) {
      rows.push([resolveDisplayLabel(key, val), String(val)]);
    } else {
      rows.push([resolveDisplayLabel(key, val), '']);
    }
  }

  return rows;
};

/**
 * 縦展開形式の行配列を返す。
 * Excel/CSV どちらも同じ行データを使う。
 * 戻り値: string[][] （各要素が1行）
 */
export const generateFreeAiOcrVerticalRows = (result: DocumentData): string[][] => {
  const rows: string[][] = [];

  // メタ情報
  const fileName = result.originalFiles?.[0]?.file_name ?? result.files?.[0]?.file_name ?? '';
  const uploadedAt = result.created_at?.toDate
    ? result.created_at.toDate().toLocaleString('ja-JP')
    : '';
  const updatedAt = result.updated_at?.toDate
    ? result.updated_at.toDate().toLocaleString('ja-JP')
    : '';
  const createdBy: string = result.user_name ?? '';
  const updatedBy: string = result.updated_by_name ?? '';

  rows.push(['ファイル名', fileName]);
  rows.push(['アップロード', uploadedAt]);
  if (updatedAt) rows.push(['更新日時', updatedAt]);
  if (createdBy) rows.push(['作成者', createdBy]);
  if (updatedBy) rows.push(['更新者', updatedBy]);

  // OCR結果
  const raw = result.result_data;
  const parsedResultData: Record<string, unknown> =
    typeof raw === 'string'
      ? (JSON.parse(raw) as Record<string, unknown>)
      : ((raw ?? {}) as Record<string, unknown>);
  // フリーOCRのキー表示順を復元してから縦展開する
  const resultData = orderFreeOcrResultData(
    parsedResultData,
    result.free_ocr_key_order as FreeOcrKeyOrderEntry[] | undefined,
  );

  const ocrRows = buildVerticalRows(resultData);
  for (const r of ocrRows) {
    rows.push(r as string[]);
  }

  return rows;
};

// 後方互換（FreeAiOcr.tsxの一括出力で使用）
export const generateFreeAiOcrExportData = (results: DocumentData[]): Record<string, string>[] => {
  return results.map(result => {
    const row: Record<string, string> = {
      ファイル名: result.originalFiles?.[0]?.file_name ?? result.files?.[0]?.file_name ?? '',
      アップロード日時: result.created_at?.toDate
        ? result.created_at.toDate().toLocaleString('ja-JP')
        : '',
      作成者: result.user_name ?? '',
    };

    const raw = result.result_data;
    const parsedResultData =
      typeof raw === 'string'
        ? (JSON.parse(raw) as Record<string, unknown>)
        : ((raw ?? {}) as Record<string, unknown>);
    // フリーOCRのキー表示順を復元
    const resultData = orderFreeOcrResultData(
      parsedResultData,
      result.free_ocr_key_order as FreeOcrKeyOrderEntry[] | undefined,
    );

    // 一括出力は1件1行の横持ちなので、かたまり（ネスト）は列名を連結して平坦化する。
    // 内部連番のキー（"_1" / "_g1"）はそのまま列名にせず、区別できる名前に置き換える（#3249）。
    const flatten = (data: Record<string, unknown>, prefix: string): void => {
      let noLabelCount = 0;
      for (const [key, val] of Object.entries(data)) {
        if (INTERNAL_KEYS.has(key)) continue;

        const isNoLabel = resolveDisplayLabel(key, val) === '';
        if (isPlainObject(val) && !isValueNode(val)) {
          // 名前のあるかたまりは列名に連ねる。名前が無いかたまりは階層を足さない
          flatten(val, isNoLabel ? prefix : `${prefix}${key}_`);
          continue;
        }

        if (isNoLabel) noLabelCount += 1;
        const label = isNoLabel ? `${prefix}(項目名なし)${noLabelCount}` : `${prefix}${key}`;

        if (Array.isArray(val)) {
          row[label] = JSON.stringify(val);
        } else if (isValueNode(val)) {
          row[label] = val.value != null ? String(val.value) : '';
        } else if (val != null) {
          row[label] = String(val);
        }
      }
    };
    flatten(resultData, '');

    return row;
  });
};

export const generateFreeAiOcrFileName = (
  selectedTab: number,
  fileType: (typeof exportFileType)[keyof typeof exportFileType],
  encoding: (typeof exportEncoding)[keyof typeof exportEncoding],
): string => {
  const date = new Date();
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const statusLabel = selectedTab === 2 ? '確認済' : selectedTab === 3 ? '出力済' : '読取中';
  const base = `フリーOCR_${statusLabel}_${y}${m}${d}`;

  // パターンOCRと同様、CSV のみエンコーディングを末尾に付与する（Excel は付けない）
  if (fileType === exportFileType.excel) {
    return `${base}.xlsx`;
  }
  const encodingSuffix = encoding === exportEncoding.shiftJis ? '_sjis' : '_utf8';
  return `${base}${encodingSuffix}.csv`;
};
